import React, { useState } from 'react';
import Header from '../../components/Header';
import EventHero from './components/EventHero';
import EventDetails from './components/EventDetails';
import EventOrganizer from './components/EventOrganizer';
import EventLocation from './components/EventLocation';
import EventSchedule from './components/EventSchedule';
import RegisteredVolunteers from './components/RegisteredVolunteers';
import EventTestimonials from './components/EventTestimonials';
import SimilarEvents from './components/SimilarEvents';
import RegistrationModal from './components/RegistrationModal';
import Icon from '../../components/AppIcon';

import { useNavigate } from 'react-router-dom';

const EventExperienceHub = () => {
  const navigate = useNavigate();
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  const eventData = {
    id: 1,
    title: "Community Garden Transformation Day",
    description: "Join us for a day of outdoor work transforming an abandoned lot into a thriving community garden. We'll be building raised beds, planting vegetables, and creating a beautiful space for the neighborhood.",
    fullDescription: `Join us for an inspiring day of community transformation as we convert an abandoned urban lot into a vibrant community garden that will serve hundreds of local families.\n\nThis hands-on event brings together volunteers of all skill levels to create raised garden beds, plant seasonal vegetables, install irrigation systems, and build gathering spaces. Whether you're an experienced gardener or have never touched soil before, there's a meaningful role for you.\n\nThe garden will provide fresh produce to local food banks, offer educational workshops for children, and create a beautiful green space where neighbors can connect. Your work today will have lasting impact for years to come.\n\nWe'll provide all tools, materials, and refreshments. Come ready to get your hands dirty, meet amazing people, and see the immediate results of your efforts. This is community building at its finest.`,
    category: "Environmental",
    status: "upcoming",
    date: "2025-12-28T09:00:00",
    dateFormatted: "Saturday, December 28, 2025",
    time: "9:00 AM - 4:00 PM",
    location: "Riverside Community Center",
    registeredVolunteers: 42,
    maxVolunteers: 60,
    impactGoal: "Transform 5,000 sq ft into productive garden space serving 200+ families",
    heroImage: "https://img.rocket.new/generatedImages/rocket_gen_img_130229536-1764666625703.png",
    heroImageAlt: "Diverse group of volunteers working together in sunny community garden with raised beds and green plants",
    featuredImage: "https://images.unsplash.com/photo-1622123636568-00845a865434",
    featuredImageAlt: "Volunteers of different ages planting seedlings in wooden raised garden beds with tools and soil visible",
    activities: [
      "Build and install 20 raised garden beds using sustainable materials",
      "Plant seasonal vegetables including tomatoes, lettuce, and herbs",
      "Install drip irrigation system for water conservation",
      "Create pathways and seating areas for community gathering",
      "Paint murals and install educational signage",
      "Organize tools and establish maintenance schedule"],

    whatToBring: [
      "Work gloves (extras provided)",
      "Water bottle and sunscreen",
      "Closed-toe shoes",
      "Hat and comfortable clothes",
      "Lunch (snacks provided)",
      "Positive attitude and energy"],

    requirements: [
      "Minimum age 16 (ages 12-15 with parent/guardian)",
      "Ability to perform moderate physical activity including lifting up to 30 lbs",
      "Commitment to full day participation (9 AM - 4 PM)",
      "Completed liability waiver and background check"],

    skillsDeveloped: [
      "Sustainable Gardening",
      "Carpentry Basics",
      "Project Management",
      "Teamwork",
      "Community Organizing",
      "Environmental Stewardship"]

  };

  const organizerData = {
    name: "Green Future Initiative",
    tagline: "Building sustainable communities one garden at a time",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11d2a1522-1764749860533.png",
    logoAlt: "Green Future Initiative logo featuring stylized plant growing from hands in circular design",
    location: "Riverside, CA",
    volunteersServed: "2,500",
    eventsHosted: "47",
    impactScore: "9.4/10",
    rating: "4.9★",
    description: "Green Future Initiative is a nonprofit organization dedicated to creating sustainable urban green spaces that bring communities together. Since 2018, we've transformed 15 abandoned lots into thriving community gardens, providing fresh produce to over 5,000 families and creating beautiful gathering spaces for neighborhoods."
  };

  const locationData = {
    venue: "Riverside Community Center",
    address: "1234 River Road, Riverside, CA 92501",
    directions: "Located 2 blocks east of Main Street. Look for the large brick building with green awnings. Check-in will be at the main entrance.",
    parking: "Free parking available in the community center lot. Additional street parking on River Road and Oak Avenue.",
    coordinates: {
      lat: 33.9533,
      lng: -117.3962
    }
  };

  const scheduleData = [
    {
      time: "8:30 AM",
      duration: "30 min",
      activity: "Check-in & Registration",
      description: "Sign in, receive your volunteer t-shirt, and enjoy coffee and pastries",
      location: "Main Entrance"
    },
    {
      time: "9:00 AM",
      duration: "30 min",
      activity: "Welcome & Orientation",
      description: "Meet the team, learn about the project, and get safety briefing",
      location: "Community Hall"
    },
    {
      time: "9:30 AM",
      duration: "2.5 hours",
      activity: "Morning Work Session",
      description: "Team assignments and hands-on garden construction work",
      location: "Garden Site"
    },
    {
      time: "12:00 PM",
      duration: "1 hour",
      activity: "Lunch Break",
      description: "Catered lunch provided, time to rest and socialize",
      location: "Picnic Area"
    },
    {
      time: "1:00 PM",
      duration: "2.5 hours",
      activity: "Afternoon Work Session",
      description: "Continue building, planting, and finishing touches",
      location: "Garden Site"
    },
    {
      time: "3:30 PM",
      duration: "30 min",
      activity: "Celebration & Photos",
      description: "Admire our work, group photos, and closing remarks",
      location: "Garden Site"
    }];


  const registeredVolunteersData = [
    {
      id: 1,
      name: "Sarah Martinez",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a73b5e15-1763293788587.png",
      avatarAlt: "Professional headshot of Hispanic woman with long brown hair wearing blue blazer smiling warmly",
      role: "Team Leader",
      isTeamLead: true
    },
    {
      id: 2,
      name: "James Chen",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b76d09ba-1763296280585.png",
      avatarAlt: "Professional photo of Asian man with short black hair in gray suit with friendly expression",
      role: "Construction Team",
      isTeamLead: false
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11176ce80-1763296378826.png",
      avatarAlt: "Headshot of young Latina woman with curly dark hair wearing green top with bright smile",
      role: "Planting Team",
      isTeamLead: false
    },
    {
      id: 4,
      name: "Michael Thompson",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d5bd88d-1763296665838.png",
      avatarAlt: "Professional image of African American man with short hair in navy shirt looking confident",
      role: "Team Leader",
      isTeamLead: true
    },
    {
      id: 5,
      name: "Lisa Patel",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff5a7c5b-1763301064209.png",
      avatarAlt: "Portrait of Indian woman with long black hair wearing red blouse with warm smile",
      role: "Irrigation Team",
      isTeamLead: false
    },
    {
      id: 6,
      name: "David Kim",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_141b19441-1763293306950.png",
      avatarAlt: "Headshot of Korean man with glasses and short dark hair in white shirt looking professional",
      role: "Construction Team",
      isTeamLead: false
    },
    {
      id: 7,
      name: "Amanda Foster",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_195842b16-1765542111989.png",
      avatarAlt: "Photo of Caucasian woman with blonde hair in ponytail wearing yellow top with cheerful expression",
      role: "Signage Team",
      isTeamLead: false
    },
    {
      id: 8,
      name: "Carlos Mendez",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1edd0d618-1763293656723.png",
      avatarAlt: "Portrait of Latino man with short dark hair and beard wearing blue shirt with friendly smile",
      role: "Planting Team",
      isTeamLead: false
    }];


  const testimonialsData = [
    {
      id: 1,
      name: "Jennifer Walsh",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10fffe6c8-1763300890025.png",
      avatarAlt: "Headshot of middle-aged Caucasian woman with shoulder-length brown hair wearing purple sweater smiling warmly",
      rating: 5,
      comment: "This was my first volunteer event and it exceeded all expectations. The organization was flawless, the team leaders were supportive, and seeing the garden come together in one day was absolutely magical. I've already signed up for the next event!",
      eventAttended: "Spring Garden Build 2025"
    },
    {
      id: 2,
      name: "Marcus Johnson",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e66e052e-1763296758737.png",
      avatarAlt: "Professional photo of African American man with short graying hair wearing green polo shirt with confident smile",
      rating: 5,
      comment: "As someone with carpentry experience, I appreciated how they utilized everyone's skills effectively. The project was well-planned, materials were quality, and the impact on the community was immediate and visible. Highly recommend!",
      eventAttended: "Fall Garden Expansion 2024"
    },
    {
      id: 3,
      name: "Priya Sharma",
      avatar: "https://images.unsplash.com/photo-1729101143873-d80050bae219",
      avatarAlt: "Portrait of young Indian woman with long dark hair wearing orange traditional top with bright smile",
      rating: 5,
      comment: "Brought my teenage daughter and we both had an amazing experience. It was educational, physically engaging, and emotionally rewarding. The lunch was delicious and we made friends with other volunteers. Can't wait to return!",
      eventAttended: "Summer Youth Garden Day 2024"
    }];


  const similarEventsData = [
    {
      id: 2,
      title: "River Cleanup & Restoration",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_104b858d4-1765713655565.png",
      imageAlt: "Group of volunteers in safety vests collecting trash along riverbank with bags and grabber tools",
      category: "Environmental",
      date: "January 11, 2026",
      location: "Riverside Park",
      volunteers: 30
    },
    {
      id: 3,
      title: "Community Mural Painting",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5c4982e-1765612617526.png",
      imageAlt: "Diverse volunteers painting colorful community mural on brick wall with paint brushes and supplies",
      category: "Arts & Culture",
      date: "January 18, 2026",
      location: "Downtown Arts District",
      volunteers: 25
    },
    {
      id: 4,
      title: "Food Bank Sorting Day",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ace9546b-1765571512028.png",
      imageAlt: "Volunteers in warehouse organizing food donations into boxes with shelves of supplies in background",
      category: "Hunger Relief",
      date: "January 25, 2026",
      location: "Central Food Bank",
      volunteers: 40
    }];


  const handleRegister = () => {
    setIsRegistrationModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="main-content">
        <EventHero event={eventData} onRegister={handleRegister} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <EventDetails event={eventData} />
              <EventSchedule schedule={scheduleData} />
              <EventTestimonials testimonials={testimonialsData} />
            </div>

            <div className="space-y-8">
              <EventOrganizer organizer={organizerData} />
              <EventLocation location={locationData} />
              <RegisteredVolunteers
                volunteers={registeredVolunteersData}
                totalCount={eventData?.registeredVolunteers}
                maxCount={eventData?.maxVolunteers} />

              <SimilarEvents events={similarEventsData} />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
            <Icon name="Heart" size={48} className="mx-auto" />
            <h2 className="text-3xl lg:text-4xl font-bold">Ready to Make a Difference?</h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Join {eventData?.registeredVolunteers} volunteers who have already registered for this transformative community event.
            </p>
            <div className="flex flex-wrap gap-4 justify-center pt-4">
              <button
                onClick={handleRegister}
                className="px-8 py-4 bg-white text-[var(--color-primary)] rounded-lg font-semibold hover:bg-white/90 transition-colors shadow-lg">

                Register Now
              </button>
              <button className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </main>
      <footer className="bg-[var(--color-card)] border-t border-[var(--color-border)] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
                  <Icon name="Heart" size={20} color="white" />
                </div>
                <span className="text-xl font-bold text-[var(--color-foreground)]">VolunteerHub</span>
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Connecting passionate volunteers with meaningful opportunities to create lasting community impact.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-[var(--color-foreground)] mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
                <li><button onClick={() => navigate('/smart-opportunity-finder')} className="hover:text-[var(--color-primary)] text-left">Find Events</button></li>
                <li><button onClick={() => navigate('/organization-impact-profiles')} className="hover:text-[var(--color-primary)] text-left">Organizations</button></li>
                <li><button onClick={() => navigate('/volunteer-journey-tracker')} className="hover:text-[var(--color-primary)] text-left">My Journey</button></li>
                <li><button onClick={() => navigate('/skills-development-academy')} className="hover:text-[var(--color-primary)] text-left">Skills Academy</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-[var(--color-foreground)] mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-[var(--color-muted-foreground)]">
                <li><a href="#" className="hover:text-[var(--color-primary)]">Help Center</a></li>
                <li><a href="#" className="hover:text-[var(--color-primary)]">Safety Guidelines</a></li>
                <li><a href="#" className="hover:text-[var(--color-primary)]">Contact Us</a></li>
                <li><a href="#" className="hover:text-[var(--color-primary)]">FAQs</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-[var(--color-foreground)] mb-4">Connect</h3>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 rounded-lg bg-[var(--color-muted)] hover:bg-[var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                  <Icon name="Facebook" size={20} />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-[var(--color-muted)] hover:bg-[var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="w-10 h-10 rounded-lg bg-[var(--color-muted)] hover:bg-[var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                  <Icon name="Instagram" size={20} />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[var(--color-border)] mt-8 pt-8 text-center text-sm text-[var(--color-muted-foreground)]">
            <p>&copy; {new Date()?.getFullYear()} VolunteerHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        eventTitle={eventData?.title} />

    </div>);

};

export default EventExperienceHub;